<script setup lang="ts">
/**
 * Module: web/src/components/BaseModal.vue
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.1.0
 * Date: 2026-07-11
 *
 * Modal shell (pattern: firstline CallDetailModal). Mobile-first per doc 04
 * sect 9.2: renders as a FULL-SCREEN SHEET below the md breakpoint and a
 * centered dialog on desktop. Esc + backdrop-click close.
 *
 * v1.1.0 (P8 polish): 150ms open/close transition -- backdrop fades, panel
 * rises 8px. Subtle by design; zeroed for reduced-motion users via the
 * global kill switch in main.css.
 */
import { onMounted, onUnmounted } from 'vue';
import AppIcon from '@/components/AppIcon.vue';

const props = defineProps<{
  open: boolean;
  title: string;
  /** Tailwind max-width class for the desktop dialog (default max-w-2xl). */
  maxWidth?: string;
}>();

const emit = defineEmits<{ (e: 'close'): void }>();

function escHandler(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.open) emit('close');
}
onMounted(() => window.addEventListener('keydown', escHandler));
onUnmounted(() => window.removeEventListener('keydown', escHandler));
</script>

<template>
  <Transition name="modal">
  <div
    v-if="open"
    class="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex md:items-center md:justify-center md:p-4"
    @click.self="emit('close')"
  >
    <div
      class="bg-card border-border-c flex flex-col w-full h-full md:h-auto md:max-h-[92vh] md:rounded-lg md:border md:shadow-2xl"
      :class="maxWidth ?? 'md:max-w-2xl'"
    >
      <header class="px-4 md:px-5 py-3 border-b border-border-c flex items-center justify-between flex-shrink-0 gap-3">
        <h2 class="font-semibold text-fg truncate">{{ title }}</h2>
        <button
          class="p-2.5 -mr-1.5 rounded-lg hover:bg-input text-fg-muted hover:text-fg transition-colors"
          aria-label="Close"
          @click="emit('close')"
        >
          <AppIcon name="close" class="w-5 h-5" />
        </button>
      </header>
      <div class="flex-1 overflow-y-auto p-4 md:p-5">
        <slot />
      </div>
      <footer v-if="$slots.footer" class="px-4 md:px-5 py-3 border-t border-border-c flex-shrink-0">
        <slot name="footer" />
      </footer>
    </div>
  </div>
  </Transition>
</template>

<style scoped>
/* Backdrop fades; the panel (direct child) rises slightly. */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 150ms ease;
}
.modal-enter-active > div,
.modal-leave-active > div {
  transition: transform 150ms ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-from > div,
.modal-leave-to > div {
  transform: translateY(8px);
}
</style>
